import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_VIDEO_CONFERENCE = gql`
  query GetVideoConference($id: ID!) {
    getVideoConference(id: $id) {
      id
      title
      status
      scheduledStartTime
      actualStartTime
      actualEndTime
      duration
      host {
        id
        firstName
        lastName
      }
      client {
        id
        firstName
        lastName
      }
      guestName
      isRecordingEnabled
      recordingStatus
      recordingS3Key
    }
  }
`;

const GET_RECORDING_URL = gql`
  query GetVideoConferenceRecordingUrl($id: ID!) {
    getVideoConferenceRecordingUrl(id: $id)
  }
`;

interface VideoConference {
  id: string;
  title: string;
  status: string;
  scheduledStartTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  duration?: number;
  host?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  client?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  guestName?: string;
  isRecordingEnabled: boolean;
  recordingStatus?: string;
  recordingS3Key?: string;
}

interface GetVideoConferenceData {
  getVideoConference: VideoConference;
}

interface GetRecordingUrlData {
  getVideoConferenceRecordingUrl: string;
}

interface RecordingModalProps {
  conferenceId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function RecordingModal({ conferenceId, isOpen, onClose }: RecordingModalProps) {
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);

  const { data: conferenceData, loading: conferenceLoading } = useQuery<GetVideoConferenceData>(GET_VIDEO_CONFERENCE, {
    variables: { id: conferenceId },
    skip: !conferenceId || !isOpen,
  });

  const { data: recordingData, loading: recordingLoading, error: recordingError } = useQuery<GetRecordingUrlData>(GET_RECORDING_URL, {
    variables: { id: conferenceId },
    skip: !conferenceId || !isOpen,
    fetchPolicy: 'network-only',
  });

  const conference = conferenceData?.getVideoConference;

  useEffect(() => {
    if (recordingData?.getVideoConferenceRecordingUrl) {
      setRecordingUrl(recordingData.getVideoConferenceRecordingUrl);
    }
  }, [recordingData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRecordingUrl(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins} min`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden mx-4">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">
              {conference?.title || 'Recording'}
            </h2>
            <p className="text-gray-400 text-sm">Video Recording</p>
          </div>
          <div className="flex items-center gap-3">
            {recordingUrl && (
              <a
                href={recordingUrl}
                download
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-aalaw-red text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </a>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Loading */}
          {(conferenceLoading || recordingLoading) && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-gray-400">Loading recording...</p>
              </div>
            </div>
          )}

          {/* Error - No recording */}
          {!conferenceLoading && !recordingLoading && (!conference || !conference.isRecordingEnabled || conference.recordingStatus !== 'COMPLETED') && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-white font-medium mb-2">Recording Not Available</h3>
                <p className="text-gray-400 text-sm">
                  {!conference 
                    ? 'Conference not found.'
                    : !conference.isRecordingEnabled
                    ? 'Recording was not enabled.'
                    : conference.recordingStatus === 'PROCESSING'
                    ? 'Recording is still processing. Check back later.'
                    : 'Recording is not available yet.'}
                </p>
              </div>
            </div>
          )}

          {/* Error fetching URL */}
          {!conferenceLoading && !recordingLoading && recordingError && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-white font-medium mb-2">Error Loading Recording</h3>
                <p className="text-gray-400 text-sm">{recordingError.message}</p>
              </div>
            </div>
          )}

          {/* Video Player */}
          {!conferenceLoading && !recordingLoading && recordingUrl && conference?.recordingStatus === 'COMPLETED' && (
            <>
              <video
                src={recordingUrl}
                controls
                autoPlay={false}
                className="w-full rounded-lg bg-black"
                style={{ maxHeight: '60vh' }}
              >
                Your browser does not support the video tag.
              </video>

              {/* Details */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="text-gray-300">{formatDateTime(conference.actualStartTime || conference.scheduledStartTime)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="text-gray-300">{formatDuration(conference.duration || 0)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Host</p>
                  <p className="text-gray-300">
                    {conference.host ? `${conference.host.firstName} ${conference.host.lastName}` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Participant</p>
                  <p className="text-gray-300">
                    {conference.client 
                      ? `${conference.client.firstName} ${conference.client.lastName}`
                      : conference.guestName || 'N/A'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
